import type { InboxItem } from '../fixtures/domain.js';
import { includesFold } from '../util/text.js';
import type { Guest } from './draft.js';

/**
 * Who a Chat Day can be sent to, and how a search decides it found them.
 *
 * One module because the picker asks the same two questions from three places —
 * of a chat row, of a saved list's members, and of the server's name hits — and
 * a rule with three homes is a rule that drifts. The mock reimplemented
 * name-or-job inline inside its picker while carrying `matchesContactSearch`
 * next door; this is that mistake, not made.
 */

/**
 * Whether an invite can be placed in this chat at all.
 *
 * `open` only. A chat in `closing` is already on a deletion countdown and one in
 * `pending_delete` is going; an invite placed in either lands in a thread about
 * to vanish, which is worse than not offering the person.
 *
 * Applied on both sources. The Chats query asks the server for `open` and gets
 * it, but a saved list holds raw channel ids with no lifecycle on them — so a
 * list built weeks ago can name chats since ended, and without this they walk
 * back in through the door.
 */
export function isInvitable(item: InboxItem): boolean {
  return item.channel.phase === 'open';
}

/**
 * Whether a row answers what was typed: the person's name, or the job their
 * chat is bound to.
 *
 * `needle` is expected already trimmed and lowercased — the caller does it once
 * per keystroke rather than once per row.
 */
export function matchesInvitable(item: InboxItem, needle: string): boolean {
  return (
    includesFold(item.counterpart.name, needle) ||
    includesFold(item.channel.jobMeta?.jobTitle, needle)
  );
}

/** The line under the name: the posting, or what a chat with no posting is. */
export function subtitleOf(item: InboxItem): string {
  return item.channel.jobMeta?.jobTitle ?? 'Direct message';
}

/** A chat, as the guest it would put on the draft. */
export function guestOf(item: InboxItem): Guest {
  return {
    id: item.counterpart.id,
    name: item.counterpart.name,
    ...(item.counterpart.avatarUrl ? { avatarUrl: item.counterpart.avatarUrl } : {}),
    ...(item.counterpart.locked ? { locked: true } : {}),
    channelId: item.channel.id,
  };
}
